import { COLORS, Tabs, Text } from "@asayinc/component-library";
import { AccountBalance } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";

import {
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

import { BackLink } from "src/components/atoms/BackLink";
import { PageContainer } from "src/components/containers/PageContainer";
import { ISSUER_TABS } from "src/constants/tabs";
import { useIssuerQuery } from "src/store/issuer";

export type IssuerView =
  | "proxy-events"
  | "bankruptcy-events"
  | "class-action-events"
  | "reports";

export const ISSUER_VIEW: { [key: string]: IssuerView } = {
  PROXY_EVENTS: "proxy-events",
  BANKRUPTCY_EVENTS: "bankruptcy-events",
  CLASS_ACTION_EVENTS: "class-action-events",
  REPORTS: "reports",
};

export function Issuer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams() as { id: string };
  const match = useMatch(`/issuers/${id}/:activeTab`) as {
    params: { activeTab: string };
  };
  const { data: issuer } = useIssuerQuery({ id });

  const handleTabChange = (value: string) => {
    if (value === "reports") {
      navigate(`/issuers/${id}/reports/share-range-analysis-events`, {
        state: { ...location.state },
      });
    } else {
      navigate(`/issuers/${id}/${value}`, { state: { ...location.state } });
    }
  };

  const securitiesWithTickers = issuer?.securities?.filter(
    (security) => security.ticker && security.ticker !== ""
  );
  const tickers = securitiesWithTickers
    ?.map((security) => security.ticker)
    .join(", ");

  const activeTab =
    match?.params?.activeTab ||
    (location.pathname.includes("reports") ? "reports" : "proxy-events");

  return (
    <PageContainer>
      <BackLink sx={{ position: "absolute", pl: 8, pt: 10 }} />
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ px: 10, pt: 10 }}
      >
        <Box
          sx={{
            borderRadius: "50%",
            height: "48px",
            width: "48px",
            mb: 3,
            backgroundColor: COLORS.BLACK,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AccountBalance sx={{ color: COLORS.WHITE }} />
        </Box>
        <Text
          data-testid="issuer-name"
          loadingProps={{ sx: { width: "300px" } }}
          variant="h5"
        >
          {issuer?.name}
        </Text>
        {tickers && tickers.length > 0 && (
          <Box
            sx={{
              display: "flex",
              mt: 3,
              textAlign: "center",
              alignItems: "center",
            }}
          >
            <Text
              loadingProps={{ sx: { width: "300px" } }}
              variant="body1"
              sx={{ mr: 1.5 }}
            >
              Ticker:
            </Text>
            <Text loadingProps={{ sx: { width: "300px" } }} variant="subtitle1">
              {tickers}
            </Text>
          </Box>
        )}
      </Stack>
      <Box sx={{ mx: 10, mt: 6 }}>
        <Tabs
          activeTab={activeTab}
          centered
          clickHandler={handleTabChange}
          tabs={ISSUER_TABS}
        />
      </Box>
      <Outlet />
    </PageContainer>
  );
}
